import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Card, Callout, Spinner, Badge, TextField, Select, Modal } from "@/design/components";
import { ApiError } from "@/api/client";
import { useAuth } from "@/app/auth";
import {
  listMembers,
  inviteMember,
  removeMember,
  changeMemberRole,
  type Member,
  type OrgRole,
  type InvitableRole,
  type InviteMemberInput,
} from "@/api/members";
import styles from "./TeamScreen.module.css";

const apiMessage = (err: unknown, fallback: string): string =>
  err instanceof ApiError ? err.message : fallback;

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

const ROLE_BADGE: Record<OrgRole, { tone: "accent" | "success" | "neutral"; label: string }> = {
  owner: { tone: "accent", label: "Owner" },
  admin: { tone: "success", label: "Admin" },
  member: { tone: "neutral", label: "Member" },
};

/**
 * Team (Story 3.4 / FR11) — list the org's admins, invite a new member (emails an
 * accept-invite link), and remove one. Role-aware: an owner badge is shown, only
 * an owner can remove an owner, you can't remove yourself here, and the API's
 * last-admin guard surfaces in the confirm modal.
 */
export function TeamScreen() {
  const qc = useQueryClient();
  const { admin } = useAuth();
  const meId = admin?.id;

  const membersQuery = useQuery({
    queryKey: ["members"],
    queryFn: ({ signal }) => listMembers(signal),
  });
  const { data: members, isPending, isError, error } = membersQuery;
  const meRole = members?.find((m) => m.id === meId)?.role;

  const [inviteOpen, setInviteOpen] = useState(false);
  const [invited, setInvited] = useState<Member | null>(null);
  const [toRemove, setToRemove] = useState<Member | null>(null);

  const invite = useMutation({
    mutationFn: (input: InviteMemberInput) => inviteMember(input),
    onSuccess: (member) => {
      void qc.invalidateQueries({ queryKey: ["members"] });
      setInvited(member);
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => removeMember(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["members"] });
      setToRemove(null);
    },
  });
  // Promote/demote happens inline (no modal — it's reversible). Errors surface in a
  // page-level Callout; the list refetches so the badge reflects the server state.
  const changeRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: InvitableRole }) => changeMemberRole(id, role),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["members"] }),
  });

  function openInvite() {
    invite.reset();
    setInvited(null);
    setInviteOpen(true);
  }
  function closeInvite() {
    if (invite.isPending) return; // don't drop an in-flight invite
    setInviteOpen(false);
  }
  function openRemove(m: Member) {
    remove.reset(); // clear a prior failure so it can't leak into this confirm
    setToRemove(m);
  }
  function closeRemove() {
    if (remove.isPending) return;
    remove.reset();
    setToRemove(null);
  }

  const canManage = !isPending && !isError;

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <p className="u-eyebrow">Control plane</p>
          <h1 className={styles.h1}>Team</h1>
        </div>
        {canManage && (
          <Button onClick={openInvite} iconBefore={<PlusIcon />} disabled title="Team invitations coming soon">
            Invite member
          </Button>
        )}
      </header>

      {isPending && (
        <div className={styles.loading}>
          <Spinner size={22} color="var(--accent)" /> <span>Loading team…</span>
        </div>
      )}

      {isError && (
        <Callout tone="danger" title="Couldn't load your team">
          {apiMessage(error, "Please try again shortly.")}
        </Callout>
      )}

      {changeRole.isError && (
        <Callout tone="danger" title="Couldn't change the role">
          {apiMessage(changeRole.error, "Please try again.")}
        </Callout>
      )}

      {members && members.length > 0 && (
        <ul className={styles.list}>
          {members.map((m) => {
            const isMe = m.id === meId;
            // Only an owner may remove an owner; you can't remove yourself here.
            const ownerLocked = m.role === "owner" && meRole !== "owner";
            // Invited-but-not-yet-accepted (COG-7a): show a Pending badge and suppress
            // role/remove actions — a pending invite isn't a manageable active member.
            const isPendingMember = m.pending === true;
            const badge = ROLE_BADGE[m.role];
            return (
              <li key={m.id}>
                <Card className={styles.row}>
                  <div className={styles.rowMain}>
                    <div className={styles.nameLine}>
                      <span className={styles.name}>{m.name?.trim() || m.email}</span>
                      <Badge tone={badge.tone}>{badge.label}</Badge>
                      {isPendingMember && <Badge tone="warning">Pending</Badge>}
                      {isMe && <Badge tone="neutral">You</Badge>}
                    </div>
                    <p className={styles.meta}>
                      {m.email} · {isPendingMember ? "invited" : "joined"} {formatDate(m.createdAt)}
                    </p>
                  </div>
                  <div className={styles.rowActions}>
                    {/* Promote/demote — never for the owner (role fixed), yourself, or a pending invite. */}
                    {!isMe && !isPendingMember && m.role !== "owner" && (
                      <Button
                        variant="secondary"
                        size="sm"
                        loading={changeRole.isPending && changeRole.variables?.id === m.id}
                        onClick={() => changeRole.mutate({ id: m.id, role: m.role === "admin" ? "member" : "admin" })}
                      >
                        {m.role === "admin" ? "Make member" : "Make admin"}
                      </Button>
                    )}
                    {!isMe && !isPendingMember && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => openRemove(m)}
                        disabled={ownerLocked}
                        title={ownerLocked ? "Only the organization owner can remove an owner" : undefined}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      {/* Invite member */}
      <Modal
        open={inviteOpen}
        onClose={closeInvite}
        title={invited ? "Invitation sent" : "Invite a member"}
        footer={invited ? <Button onClick={closeInvite}>Done</Button> : undefined}
      >
        {invited ? (
          <div className={styles.success}>
            <p>
              We've emailed <strong>{invited.email}</strong> an invite as{" "}
              <strong>{ROLE_BADGE[invited.role].label.toLowerCase()}</strong>. They set their own password
              from the link to join your organization.
            </p>
            <Callout tone="info">
              The invite link expires for security. If it lapses, just invite them again.
            </Callout>
          </div>
        ) : (
          <InviteMemberForm
            pending={invite.isPending}
            error={invite.isError ? apiMessage(invite.error, "Couldn't send the invite.") : null}
            onSubmit={(values) => invite.mutate(values)}
          />
        )}
      </Modal>

      {/* Remove confirm */}
      <Modal
        open={toRemove !== null}
        onClose={closeRemove}
        title="Remove member?"
        footer={
          <>
            <Button variant="ghost" onClick={closeRemove} disabled={remove.isPending}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => toRemove && remove.mutate(toRemove.id)}
            >
              Remove
            </Button>
          </>
        }
      >
        <p>
          Remove <strong>{toRemove?.name?.trim() || toRemove?.email}</strong> from your organization?
          They lose admin access immediately. This can't be undone (you can re-invite them later).
        </p>
        {remove.isError && (
          <Callout tone="danger">{apiMessage(remove.error, "Couldn't remove the member.")}</Callout>
        )}
      </Modal>
    </div>
  );
}

// --- Invite form (RHF + Zod, mirrors api inviteSchema: email required, name
//     optional, role admin|member defaulting to admin). Unmounts on modal close. ---
const inviteSchema = z.object({
  email: z.string().trim().email("Enter a valid email"),
  name: z.string().trim().optional(),
  role: z.enum(["admin", "member"]),
});
type InviteValues = z.infer<typeof inviteSchema>;

function InviteMemberForm({
  pending,
  error,
  onSubmit,
}: {
  pending: boolean;
  error: string | null;
  onSubmit: (values: InviteValues) => void;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<InviteValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { email: "", name: "", role: "admin" },
  });

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
      {error && (
        <div role="alert">
          <Callout tone="danger">{error}</Callout>
        </div>
      )}
      <TextField
        label="Email"
        type="email"
        autoComplete="off"
        placeholder="teammate@acme.com"
        required
        error={errors.email?.message}
        {...register("email")}
      />
      <TextField label="Name" placeholder="Optional" error={errors.name?.message} {...register("name")} />
      <Select
        label="Role"
        required
        hint="Admins manage channels, team, and billing. Members have restricted access."
        options={[
          { value: "admin", label: "Admin" },
          { value: "member", label: "Member" },
        ]}
        error={errors.role?.message}
        {...register("role")}
      />
      <Button type="submit" fullWidth loading={pending}>
        Send invite
      </Button>
    </form>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" fill="none" aria-hidden="true">
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}
